/**
 * ConsentForm - reusable parental waiver signing form.
 *
 * Renders the waiver document in a scrollable panel (checkboxes enable only after the guardian has
 * scrolled through the full disclosure, per UC-129), a per-consent-type checklist (partial consent
 * supported), and a typed-name electronic signature. Calls onSubmit({ consentTypes, signatureName }).
 */
import { useState, useRef, useMemo } from 'react'
import { Loader2 } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { CONSENT_TYPE_LABELS } from '../../services/consentService'

const ConsentForm = ({
  document,
  studentName,
  onSubmit,
  processing = false,
  submitLabel = 'Submit Consent'
}) => {
  const availableTypes = useMemo(() => document?.consentTypes || [], [document])
  const [scrolledToEnd, setScrolledToEnd] = useState(false)
  const [checked, setChecked] = useState({})
  const [signature, setSignature] = useState('')
  const scrollRef = useRef(null)

  const handleScroll = () => {
    const el = scrollRef.current
    if (!el) return
    // Enable once the guardian has scrolled within 24px of the bottom
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 24) {
      setScrolledToEnd(true)
    }
  }

  const toggleType = (type) => setChecked((prev) => ({ ...prev, [type]: !prev[type] }))

  const selectedTypes = availableTypes.filter((t) => checked[t])
  const canSubmit =
    scrolledToEnd && selectedTypes.length > 0 && signature.trim().length >= 2 && !processing

  const handleSubmit = () => {
    if (!canSubmit) return
    onSubmit({ consentTypes: selectedTypes, signatureName: signature.trim() })
  }

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {document?.title}
        </h3>
        {studentName && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            For student: <strong>{studentName}</strong>
          </p>
        )}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="h-72 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/40"
        >
          {/* react-markdown so the legal text renders ordered/nested lists, inline code, etc.
              correctly. prose styling matches the document rendering used elsewhere. react-markdown
              does NOT render raw HTML by default (no rehype-raw is used), so markdown can't inject
              scripts; disallowedElements is belt-and-suspenders against any future raw-HTML plugin. */}
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown
              disallowedElements={['script', 'iframe', 'object', 'embed', 'style']}
              unwrapDisallowed
            >
              {document?.bodyMarkdown || ''}
            </ReactMarkdown>
          </div>
        </div>
        {!scrolledToEnd && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
            Please scroll through the full document to continue.
          </p>
        )}
      </div>

      <fieldset disabled={!scrolledToEnd} className={scrolledToEnd ? '' : 'opacity-50'}>
        <legend className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
          I authorize the following (select all that apply):
        </legend>
        <div className="space-y-2">
          {availableTypes.map((type) => (
            <label
              key={type}
              className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300"
            >
              <input
                type="checkbox"
                checked={!!checked[type]}
                onChange={() => toggleType(type)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              {CONSENT_TYPE_LABELS[type] || type}
            </label>
          ))}
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
            Electronic signature (type your full legal name)
          </label>
          <input
            type="text"
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            placeholder="Full legal name"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Typing your name and submitting is your legally binding electronic signature
            (E-SIGN/UETA).
          </p>
        </div>
      </fieldset>

      <button
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {processing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
            Submitting...
          </>
        ) : (
          submitLabel
        )}
      </button>
    </div>
  )
}

export default ConsentForm
